import type { ConversionTracked } from "@/hooks/eventing/types/core.conversions.ConversionTracked.v2";
import { TagularAction, TagularCategory } from "@/types/Tagular";
import type { CartItem } from "@/utils/cartUtils";
import {
  translateVariant,
  TagularVariant,
} from "@/utils/tagular/dataTranslationMatrices";

const getVariant = (productType?: string) =>
  productType
    ? (translateVariant(productType) ?? TagularVariant.Courses)
    : TagularVariant.Courses;

/**
 * Creates product view event data for a cart item
 */
export const createProductViewEventData = (
  item: CartItem,
  index: number,
  cartBrand?: string | null,
  correlationId?: string,
) => ({
  product: {
    brand: cartBrand ?? item.brand ?? "edX",
    category: item.type ?? "course",
    formatSubtype: "modal",
    formatType: "abandoned-cart",
    location: "abandoned-cart-modal",
    name: item.courseName,
    position: index + 1,
    productId: item.id,
    variant: getVariant(item.type),
  },
  viewCorrelationId: correlationId || "multi-cart-modal",
});

/**
 * Creates element viewed event data for a cart item
 */
export const createElementViewEventData = (
  item: CartItem,
  index: number,
  correlationId: string,
) => ({
  webElement: {
    name: `cart-product-${item.id}`,
    htmlId: `item-${item.id}`,
    elementType: "listitem",
    location: "abandoned-cart-modal",
    position: (index + 1).toString(),
    text: item.courseName,
    variant: getVariant(item.type),
  },
  correlationId,
});

/**
 * Creates conversion event data for cart checkout
 */
export const createConversionEventData = (
  items: CartItem[],
  cartBrand?: string | null,
  correlationId?: string,
  customName = "abandoned_cart_checkout",
): ConversionTracked => ({
  correlation: {
    id: correlationId || "",
  },
  metadata: {
    customName,
    action: TagularAction.STARTED,
    category: TagularCategory.ENROLLMENT,
    timestamp: new Date().toISOString(),
    productList: items.map((item) => ({
      brand: cartBrand ?? item.brand ?? "edX",
      category: item.type ?? "course",
      variant: getVariant(item.type),
      name: item.courseName,
      id: item.courseKey,
      externalId: item.courseRunKey,
    })),
  },
});
